import { useEffect, useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Tab, Tabs, Typography } from '@mui/material'

import type { IdentityStatusHistoryItem } from '@/core/models/patient.model'
import { useGetPatientInsAuditLogsQuery } from '@/core/services/ins-audit-log'
import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'

import { IdentityAuditOperationsTable } from './IdentityAuditOperationsTable'
import { IdentityStatusChangesTable } from './IdentityStatusChangesTable'

const ROWS_PER_PAGE_OPTIONS = [5, 10, 15]

enum IdentityAuditTab {
  STATUS_CHANGES = 'status-changes',
  OPERATIONS = 'operations',
}

type PatientIdentityAuditDialogProps = {
  identityStatusHistory?: IdentityStatusHistoryItem[]
  onClose: () => void
  open: boolean
  patientId: string
}

function getTabA11yProps(tab: IdentityAuditTab) {
  return {
    id: `identity-audit-tab-${tab}`,
    'aria-controls': `identity-audit-panel-${tab}`,
  }
}

export function PatientIdentityAuditDialog({ identityStatusHistory, onClose, open, patientId }: PatientIdentityAuditDialogProps) {
  const lingui = useLingui()
  const { isINSEnabled } = useIsINSEnabled()
  const [activeTab, setActiveTab] = useState(IdentityAuditTab.OPERATIONS)
  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0])
  const isOperationsTabActive = activeTab === IdentityAuditTab.OPERATIONS

  const { data, isError, isFetching } = useGetPatientInsAuditLogsQuery(
    {
      patientId,
      limit: itemsPerPage,
      offset: (page - 1) * itemsPerPage,
    },
    { skip: !open || !isINSEnabled || !isOperationsTabActive }
  )

  useEffect(() => {
    if (!open) return
    setActiveTab(IdentityAuditTab.OPERATIONS)
    setPage(1)
  }, [open])

  useEffect(() => {
    if (!isINSEnabled && open) onClose()
  }, [isINSEnabled, onClose, open])

  const handleItemsPerPageChange = (nextItemsPerPage: number) => {
    setItemsPerPage(nextItemsPerPage)
    setPage(1)
  }

  if (!isINSEnabled) return null

  return (
    <Dialog fullWidth maxWidth="lg" onClose={onClose} open={open} aria-labelledby="patient-identity-audit-title">
      <DialogTitle id="patient-identity-audit-title">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography component="h2" variant="h6">
            <Trans>Identity audit history</Trans>
          </Typography>
          <IconButton aria-label={lingui._(t`Close`)} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs
          aria-label={lingui._(t`Identity audit history`)}
          onChange={(_event, nextTab: IdentityAuditTab) => setActiveTab(nextTab)}
          value={activeTab}
          variant="scrollable"
        >
          <Tab label={lingui._(t`Access and identity operations`)} value={IdentityAuditTab.OPERATIONS} {...getTabA11yProps(IdentityAuditTab.OPERATIONS)} />
          <Tab label={lingui._(t`Identity status changes`)} value={IdentityAuditTab.STATUS_CHANGES} {...getTabA11yProps(IdentityAuditTab.STATUS_CHANGES)} />
        </Tabs>
      </Box>

      <DialogContent dividers>
        <Box
          aria-labelledby={`identity-audit-tab-${IdentityAuditTab.STATUS_CHANGES}`}
          id={`identity-audit-panel-${IdentityAuditTab.STATUS_CHANGES}`}
          role="tabpanel"
          hidden={activeTab !== IdentityAuditTab.STATUS_CHANGES}
        >
          {activeTab === IdentityAuditTab.STATUS_CHANGES && <IdentityStatusChangesTable history={identityStatusHistory} />}
        </Box>

        <Box
          aria-labelledby={`identity-audit-tab-${IdentityAuditTab.OPERATIONS}`}
          id={`identity-audit-panel-${IdentityAuditTab.OPERATIONS}`}
          role="tabpanel"
          hidden={!isOperationsTabActive}
        >
          {isOperationsTabActive && (
            <IdentityAuditOperationsTable
              isError={isError}
              isFetching={isFetching}
              itemsPerPage={itemsPerPage}
              logs={data?.logs ?? []}
              onItemsPerPageChange={handleItemsPerPageChange}
              onPageChange={setPage}
              page={page}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              total={data?.total ?? 0}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
